import { useEffect, useState } from 'react';

import { getLoggedInFirstName, isLoggedIn as checkIsLoggedIn } from '@src/utils/auth-session';

interface UseIsLoggedInResult {
	isLoggedIn: boolean;
	firstName: string | null;
	checked: boolean;
}

// Module-level cache: both HeaderDesktop and HeaderMobile mount simultaneously.
// This ensures only one network request fires and subsequent mounts get the cached value.
let cachedFirstName: string | null = null;
let cacheResolved = false;
let pendingFetch: Promise<string | null> | null = null;

function fetchFirstNameFromDM(): Promise<string | null> {
	if (cacheResolved) return Promise.resolve(cachedFirstName);

	if (!pendingFetch) {
		pendingFetch = fetch('/api/auth/user-name')
			.then((r) => (r.ok ? r.json() : null))
			.then((data: { firstName?: string | null } | null) => {
				cachedFirstName = data?.firstName || null;
				cacheResolved = true;
				pendingFetch = null;
				return cachedFirstName;
			})
			.catch((error_: unknown) => {
				cachedFirstName = null;
				cacheResolved = true;
				pendingFetch = null;
				return null;
			});
	}

	return pendingFetch;
}

const useIsLoggedIn = (): UseIsLoggedInResult => {
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [firstName, setFirstName] = useState<string | null>(null);
	const [checked, setChecked] = useState(false);

	useEffect(() => {
		const loggedIn = checkIsLoggedIn();
		setIsLoggedIn(loggedIn);

		if (!loggedIn) {
			setChecked(true);
			return;
		}

		const storedName = getLoggedInFirstName();
		if (storedName) {
			setFirstName(storedName);
			setChecked(true);
			return;
		}

		fetchFirstNameFromDM().then((name) => {
			setFirstName(name);
			setChecked(true);
		});
	}, []);

	return { isLoggedIn, firstName, checked };
};

export default useIsLoggedIn;
